import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, Clock, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const SERVICES = [
  "Domestic Cleaning",
  "Office Cleaning",
  "End of Tenancy Cleaning",
  "Carpet Cleaning",
  "Upholstery Cleaning",
  "Residential Cleaning",
  "Laundry & Ironing",
  "Vacuuming",
  "Something else",
];

const contactCards = [
  {
    icon: Phone,
    label: "Call us",
    value: "07776 714 973",
    href: "tel:07776714973",
    testid: "contact-phone-link",
  },
  {
    icon: Mail,
    label: "Email us",
    value: "hyekconnect@gmail.com",
    href: "mailto:hyekconnect@gmail.com",
    testid: "contact-email-link",
  },
  {
    icon: Clock,
    label: "Opening hours",
    value: "Open 24 hours, 7 days a week",
    href: null,
    testid: "contact-hours",
  },
];

export const Contact = () => {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: SERVICES[0], message: "" });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Thank you! Your quote request is on its way — we'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", service: SERVICES[0], message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const field =
    "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition-shadow focus:ring-2 focus:ring-teal-500";
  const label = "text-xs font-bold uppercase tracking-[0.12em] text-slate-500";

  return (
    <section id="contact" data-testid="contact-section" className="bg-teal-50/60 py-24 lg:py-32">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 lg:grid-cols-2 lg:gap-20 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.75, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-teal-700">Get in Touch</p>
          <h2 className="font-serif text-4xl font-medium tracking-tight text-slate-900 sm:text-5xl">
            Tell us what needs <span className="italic text-teal-700">to sparkle.</span>
          </h2>
          <p className="mt-5 max-w-md text-sm leading-relaxed text-slate-500 sm:text-base">
            Request a free, no-obligation quote and we'll get back to you the same day. Prefer to
            talk? Call or email us directly — a real person always answers.
          </p>
          <div className="mt-10 flex flex-col gap-4">
            {contactCards.map((c) => {
              const inner = (
                <>
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-teal-700 text-white">
                    <c.icon className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-400">
                      {c.label}
                    </span>
                    <span className="mt-0.5 block text-base font-bold text-slate-900">{c.value}</span>
                  </span>
                </>
              );
              return c.href ? (
                <a
                  key={c.label}
                  data-testid={c.testid}
                  href={c.href}
                  className="flex items-center gap-4 rounded-2xl border border-slate-100 bg-white p-5 shadow-sm transition-transform duration-300 hover:-translate-y-0.5 hover:shadow-md"
                >
                  {inner}
                </a>
              ) : (
                <div
                  key={c.label}
                  data-testid={c.testid}
                  className="flex items-center gap-4 rounded-2xl border border-slate-100 bg-white p-5 shadow-sm"
                >
                  {inner}
                </div>
              );
            })}
          </div>
        </motion.div>

        <motion.form
          onSubmit={submit}
          data-testid="quote-form"
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.75, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-col gap-5 rounded-3xl border border-slate-100 bg-white p-8 shadow-xl lg:p-10"
        >
          <h3 className="font-serif text-2xl font-semibold tracking-tight text-slate-900">
            Get your free quote
          </h3>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="quote-name" className={label}>Name</label>
              <input
                id="quote-name"
                data-testid="quote-name-input"
                required
                minLength={2}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={field}
                placeholder="Your full name"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="quote-email" className={label}>Email</label>
              <input
                id="quote-email"
                data-testid="quote-email-input"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={field}
                placeholder="you@example.com"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="quote-phone" className={label}>Phone (optional)</label>
              <input
                id="quote-phone"
                data-testid="quote-phone-input"
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className={field}
                placeholder="07700 000000"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="quote-service" className={label}>Service needed</label>
              <select
                id="quote-service"
                data-testid="quote-service-select"
                value={form.service}
                onChange={(e) => setForm({ ...form, service: e.target.value })}
                className={field}
              >
                {SERVICES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="quote-message" className={label}>Tell us about your space</label>
            <textarea
              id="quote-message"
              data-testid="quote-message-input"
              required
              minLength={5}
              rows={4}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className={`${field} resize-none`}
              placeholder="e.g. 2-bed flat end of tenancy clean, needed next Friday…"
            />
          </div>
          <button
            data-testid="quote-submit-button"
            type="submit"
            disabled={submitting}
            className="group mt-1 flex items-center justify-center gap-2 rounded-full bg-teal-700 py-4 text-sm font-bold text-white shadow-lg shadow-teal-700/25 transition-transform duration-300 hover:scale-[1.02] hover:bg-teal-600 disabled:opacity-60"
          >
            {submitting ? "Sending…" : "Send My Free Quote Request"}
            <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <p className="text-center text-xs text-slate-400">
            No spam, no obligation — just a friendly, fast quote.
          </p>
        </motion.form>
      </div>
    </section>
  );
};
